import { Fragment, ReactNode, createContext, useCallback, useContext, useState } from 'react'
import Toast, { ToastSeverity } from '../ui/primitives/Toast'

interface ModalContextType {
  openModal: (id: string, render: ReactNode) => string
  closeModal: (id: string) => void
  showSnackbar: (
    id: string,
    message: string,
    severity?: ToastSeverity,
  ) => void
}

const ModalContext = createContext<ModalContextType>({
  openModal: () => '',
  closeModal: () => {},
  showSnackbar: () => {},
})

interface ModalProviderProps {
  children: ReactNode
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [modals, setModals] = useState<{ id: string; render: ReactNode }[]>([])

  const openModal = useCallback((id: string, render: ReactNode) => {
    setModals((prevModals) => [...prevModals, { id, render }])
    return id
  }, [])

  const closeModal = useCallback((id: string) => {
    setModals((prevModals) => prevModals.filter((m) => m.id !== id))
  }, [])

  const showSnackbar = useCallback(
    (id: string, message: string, severity: ToastSeverity = 'info') => {
      const handleClose = () => closeModal(id)
      openModal(
        id,
        <Toast message={message} severity={severity} onClose={handleClose} />,
      )
    },
    [closeModal, openModal],
  )

  return (
    <ModalContext.Provider value={{ openModal, closeModal, showSnackbar }}>
      {modals.map((modal) => (
        <Fragment key={modal.id}>{modal.render}</Fragment>
      ))}
      {children}
    </ModalContext.Provider>
  )
}

export const useModal = () => useContext(ModalContext)
